// Платформо-независимый слой над Telegram и MAX Mini Apps.
//
// Авторизация в обоих мессенджерах устроена одинаково: хост прокидывает
// в страницу мост с подписанной строкой `initData`, которую мы шлём на
// бэкенд. Имя заголовка зависит от платформы: Telegram → `X-Telegram-Init-Data`,
// MAX → `X-Max-Init-Data` (см. getInitDataHeaderName). Этот модуль прячет
// различие «где лежит мост» (window.Telegram vs window.WebApp) и нейминг
// заголовков/ws-параметров, чтобы остальной код не ветвился по платформам.
import { isTelegramMiniApp, getInitData as getTelegramInitData, waitForInitData as waitForTelegramInitData, loadTelegramWebAppScript, } from "./telegram";
import { isMaxMiniApp, getMaxInitData, waitForMaxInitData, loadMaxWebAppScript, } from "./max";
/** Имена auth-заголовков initData по платформам. */
const INIT_DATA_HEADER = {
    telegram: "X-Telegram-Init-Data",
    max: "X-Max-Init-Data",
};
/** Имена query-параметров initData для WebSocket по платформам. */
const INIT_DATA_WS_PARAM = {
    telegram: "tg_init_data",
    max: "max_init_data",
};
/** Текущая mini-app платформа, либо null (обычный браузер). */
export function getMiniAppPlatform() {
    // Telegram проверяем первым: у него больше сигналов определения
    // (UA / referrer / URL), он надёжнее различается до загрузки моста.
    if (isTelegramMiniApp())
        return "telegram";
    if (isMaxMiniApp())
        return "max";
    return null;
}
/** Запущены ли мы в каком-либо mini-app (Telegram или MAX). */
export function isAnyMiniApp() {
    return getMiniAppPlatform() !== null;
}
/**
 * Дождаться, пока определится mini-app платформа, ПЕРЕД решением «гость vs
 * мини-апп» на старте (см. AuthProvider). Нужно потому, что мост-скрипты в
 * index.html грузятся с `async` (не блокируют бандл — иначе зависший хост вешал
 * бы старт на минуты), и к первому рендеру initData может быть ещё не готов.
 * Синхронный getMiniAppPlatform() в этот момент вернул бы null → уход в гостя
 * («курица-яйцо»). Здесь мы ждём готовность.
 *
 * Возврат:
 * - платформа — как только у неё появился НЕПУСТОЙ initData (fast-path либо в
 *   ходе поллинга; на мобилке это происходит за сотни мс, как только доехал
 *   мост своей платформы — чужой зависший хост не влияет);
 * - null — как только ОБА мост-скрипта разрешились (onload/onerror, флаги в
 *   index.html), а платформа не распозналась: значит мы на обычном вебе — ждать
 *   больше нечего (веб не тормозим лишним таймаутом; onerror ловит и
 *   заблокированный/недоступный хост, который глобал не создаёт);
 * - null по таймауту — фолбэк (например, на вебе хост «висит» без onload/onerror;
 *   ждём не дольше maxWaitTime, а не минуты как раньше на defer).
 */
export async function waitForMiniAppPlatform(maxWaitTime = 3000, checkInterval = 100) {
    // «Оба моста отработали» = каждый async-скрипт РАЗРЕШИЛСЯ (onload или onerror,
    // флаги ставятся в index.html). Именно «разрешился», а не «создал глобал»:
    // заблокированный/недоступный хост (adblock, DNS-блок, RST) глобал НЕ создаёт,
    // но onerror срабатывает — иначе веб залипал бы на весь таймаут. «Висящее»
    // соединение (MAX-мобилка × telegram.org) не даёт ни того, ни другого, но там
    // платформа распознаётся по initData доступного моста раньше этой проверки.
    const bothBridgesSettled = () => typeof window !== "undefined" &&
        window.__maxBridgeSettled === 1 &&
        window.__tgBridgeSettled === 1;
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitTime) {
        // Детект платформы имеет ПРИОРИТЕТ над коротким замыканием: на десктоп-MAX
        // доступны оба хоста, и window.Telegram.WebApp (пустой) может появиться
        // одновременно с window.WebApp (уже с MAX-initData). Сначала распознаём
        // платформу, только потом делаем вывод «обычный веб».
        const platform = getMiniAppPlatform();
        if (platform)
            return platform;
        // Оба async-моста исполнились, но платформа не распозналась → обычный веб.
        if (bothBridgesSettled())
            return null;
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
    return null;
}
/**
 * Контекст мини-аппа для выбора стратегии авторизации (Bearer из localStorage
 * vs cookie на вебе): реальный TG/MAX мини-апп ИЛИ ручной класс `.miniapp` на
 * body/html. ВАЖНО: не по `Boolean(window.WebApp)` — мост MAX подключён
 * глобально в index.html и присутствует на обычном вебе (там initData пуст),
 * так что presence-проверка ловила бы веб как мини-апп и ломала cookie-флоу.
 */
export function isMiniAppContext() {
    if (isAnyMiniApp())
        return true;
    if (typeof document === "undefined")
        return false;
    return (document.body?.classList.contains("miniapp") === true ||
        document.documentElement?.classList.contains("miniapp") === true);
}
/** initData текущей платформы (для initData-заголовка). */
export function getAnyInitData() {
    if (isTelegramMiniApp())
        return getTelegramInitData();
    if (isMaxMiniApp())
        return getMaxInitData();
    return null;
}
/**
 * Имя auth-заголовка initData под текущую платформу:
 * Telegram → `X-Telegram-Init-Data`, MAX → `X-Max-Init-Data`.
 * Вне mini-app (обычный браузер) initData отсутствует, поэтому возвращаем
 * Telegram-имя как безопасный дефолт — фактически заголовок не выставляется.
 */
export function getInitDataHeaderName() {
    return INIT_DATA_HEADER[getMiniAppPlatform() ?? "telegram"];
}
/**
 * Готовый query-параметр initData для URL WebSocket под текущую платформу:
 * Telegram → `tg_init_data=...`, MAX → `max_init_data=...` (значение
 * URL-кодируется). `null`, если данных нет (обычный браузер).
 */
export function getInitDataWsParam() {
    const platform = getMiniAppPlatform();
    if (!platform)
        return null;
    const initData = getAnyInitData();
    if (!initData)
        return null;
    return `${INIT_DATA_WS_PARAM[platform]}=${encodeURIComponent(initData)}`;
}
/**
 * Подгрузить мост текущей платформы, если он ещё не инициализирован.
 * Безопасно вызывать всегда — для обычного браузера это no-op.
 */
export async function ensureMiniAppBridge() {
    if (isTelegramMiniApp()) {
        try {
            await loadTelegramWebAppScript();
        }
        catch {
            /* мост может быть уже инжектирован хостом — игнорируем */
        }
        return;
    }
    if (isMaxMiniApp()) {
        try {
            await loadMaxWebAppScript();
        }
        catch {
            /* мост может быть уже инжектирован хостом — игнорируем */
        }
    }
}
/** Дождаться initData текущей платформы (с таймаутом). */
export async function waitForAnyInitData(maxWaitTime = 3000, checkInterval = 100) {
    if (isTelegramMiniApp()) {
        return waitForTelegramInitData(maxWaitTime, checkInterval);
    }
    if (isMaxMiniApp()) {
        return waitForMaxInitData(maxWaitTime, checkInterval);
    }
    return null;
}
